// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import NoDataCell from './NoDataCell';
import { HoldingIcon } from '../common';

// Utils
import { formatLocalizedString, formatCurrency, formatMessageWithLocale } from '../../../utils/i18n';
import { PROPOSAL_ITEM_PURPOSE } from '../../../modules/proposal/utils/constants';
import { ParenthesesLabel } from '../misc';
import HypotheticalReturnLegendValue from '@/types/hypothetical-return-legend-value';
import Locale from '@/types/locale';

type Props = {
  value?: HypotheticalReturnLegendValue;
  className?: string;
  itemType: string;
  color: string;
  showBaseCurrencies?: boolean;
  locale?: Locale;
  decimalPlace?: number;
};

const HypotheticalReturnLegendCell: React.FunctionComponent<Props> = ({
  value,
  locale,
  className,
  color,
  itemType,
  showBaseCurrencies,
  decimalPlace,
}: Props) => {
  if (value == null) {
    return <NoDataCell />;
  }

  const [purposeFlagKey, purposeFlag] = Object.entries(PROPOSAL_ITEM_PURPOSE).find(([key, flag]) => value.item?.flags?.includes(flag)) ?? [];
  const itemName = value.item !== undefined ? formatLocalizedString(value.item.name, locale) : value.name;

  return (
    <div className={classNames('cell hypothetical-return-legend-cell', className)}>
      <div className="item-label">
        {purposeFlag ? (
          <div className={classNames('legend-purpose-pill', purposeFlag)} style={{ '--holding-accent-color': color } as React.CSSProperties}>
            <HoldingIcon type={itemType} />
            <span>{formatMessageWithLocale(`Proposal.ProposalItemPurpose.${purposeFlagKey}`, locale)}</span>
          </div>
        ) : (
          <HoldingIcon type={itemType} color={color} />
        )}

        <span
          className={classNames('legend-item-name', { 'font-weight-bold': purposeFlag === PROPOSAL_ITEM_PURPOSE.RECOMMENDED })}
          data-private
          data-dd-privacy="mask"
        >
          {itemName}
          {showBaseCurrencies && value.item && <ParenthesesLabel as="span" text={value.item?.currency} />}
        </span>
        {itemName && <span>&nbsp;:</span>}
      </div>

      <span className={classNames('item-value', { 'font-weight-bold': purposeFlag === PROPOSAL_ITEM_PURPOSE.RECOMMENDED })}>
        {value && formatCurrency(value.size, decimalPlace, undefined, false, locale)}
      </span>
    </div>
  );
};

export default HypotheticalReturnLegendCell;
